/**
 * Timeline cronologica de eventos.
 *
 * Backend devolve descricao ja humanizada (descricao_evento). Frontend so
 * exibe + escolhe icone/cor por categoria de tipo (projeto.* / fase.* /
 * dependencia.*).
 */

import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Play, CheckCircle, AlertTriangle, Ban, UserPlus, Trash2, Plus,
  Link2, AlertOctagon, Pause, ArrowRight, Clock,
} from 'lucide-react';
import { fasesAPI, projetosAPI } from '../../../services/inovacaoAPI';
import { mensagemErro } from '../labels';

const ICONE_POR_TIPO = {
  'projeto.criado':                { Icon: Plus, cor: 'var(--brand, #20a490)' },
  'projeto.iniciado':              { Icon: Play, cor: '#3498db' },
  'projeto.pausado':               { Icon: Pause, cor: '#9b59b6' },
  'projeto.retomado':              { Icon: Play, cor: '#3498db' },
  'projeto.cancelado':             { Icon: Ban, cor: '#95a5a6' },
  'projeto.concluido':             { Icon: CheckCircle, cor: '#27ae60' },
  'projeto.em_risco':              { Icon: AlertOctagon, cor: '#e74c3c' },
  'projeto.responsavel_atribuido': { Icon: UserPlus, cor: '#3498db' },
  'fase.iniciada':                 { Icon: Play, cor: '#3498db' },
  'fase.concluida':                { Icon: CheckCircle, cor: '#27ae60' },
  'fase.marcada_em_risco':         { Icon: AlertTriangle, cor: '#e67e22' },
  'fase.bloqueada':                { Icon: Ban, cor: '#e74c3c' },
  'fase.desbloqueada':             { Icon: Play, cor: '#3498db' },
  'fase.deletada':                 { Icon: Trash2, cor: '#95a5a6' },
  'fase.escopo_adicional':         { Icon: Plus, cor: '#ff7a00' },
  'fase.estimativa_ultrapassada':  { Icon: AlertOctagon, cor: '#e74c3c' },
  'fase.responsavel_atribuido':    { Icon: UserPlus, cor: '#3498db' },
  'dependencia.criada':            { Icon: Link2, cor: '#7f8c8d' },
  'dependencia.removida':          { Icon: Link2, cor: '#95a5a6' },
};

const FALLBACK = { Icon: Clock, cor: '#bdc3c7' };

/**
 * @param {Object} props
 * @param {('fase'|'projeto')} props.tipo
 * @param {number} props.id
 */
export default function TimelineEventos({ tipo, id }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fetcher = tipo === 'fase' ? fasesAPI.timeline : projetosAPI.timeline;
    fetcher(id)
      .then(setItems)
      .catch((e) => toast.error(mensagemErro(e, 'Erro ao carregar timeline')))
      .finally(() => setLoading(false));
  }, [tipo, id]);

  return (
    <div>
      <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#2c3e50',
                   display: 'flex', alignItems: 'center', gap: 6 }}>
        <Clock size={14} /> Timeline ({items.length})
      </h4>
      {loading && <p style={textoMuted}>Carregando…</p>}
      {!loading && items.length === 0 && (
        <p style={textoMuted}>Sem eventos ainda.</p>
      )}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.82rem',
                   maxHeight: 320, overflowY: 'auto' }}>
        {items.map((ev) => {
          const cfg = ICONE_POR_TIPO[ev.tipo] || FALLBACK;
          const { Icon } = cfg;
          return (
            <li key={ev.id} style={{ display: 'flex', gap: 8, padding: '7px 0',
                                     borderBottom: '1px solid #ecf0f1' }}>
              <div style={{ flexShrink: 0, color: cfg.cor, marginTop: 2 }}>
                <Icon size={14} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#2c3e50' }}>{ev.descricao}</div>
                <div style={{ fontSize: '0.7rem', color: '#95a5a6', marginTop: 1 }}>
                  {formatarData(ev.ocorrido_em)}
                  {ev.ator && <> · por <b style={{ color: '#7f8c8d' }}>{ev.ator.nome}</b></>}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function formatarData(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

const textoMuted = { color: '#bdc3c7', fontSize: '0.85rem' };
